import { useCallback, useEffect, useState } from "react";
import { clinicalApi, type CatheterRecord, type CurrentUser } from "../../lib/api/clinical";
import { daysSince, formatDate } from "./format";

interface Props {
  user: CurrentUser;
}

export function CathetersPanel({ user }: Props) {
  const [rows, setRows] = useState<CatheterRecord[]>([]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const canWrite = user.role === "admin" || user.role === "infection_control";

  const load = useCallback(async () => {
    const data = await clinicalApi.catheters(undefined, true);
    setRows(data);
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    load().catch(() => {
      setMessage("加载在管导管失败");
    });
  }, [load]);

  async function act(task: () => Promise<unknown>, ok: string) {
    setBusy(true);
    setMessage("");
    try {
      await task();
      await load();
      setMessage(ok);
    } catch {
      setMessage("操作失败");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="view">
      <div className="section-head">
        <h2>在管导管 · 每日必要性评估</h2>
        {message ? <span role="status">{message}</span> : null}
      </div>
      {rows.length === 0 ? (
        <p>暂无在管导管记录。</p>
      ) : (
        <table className="queue-table">
          <thead>
            <tr>
              <th>置管时间</th>
              <th>留置天数</th>
              <th>适应证</th>
              <th>今日评估</th>
              <th>建议拔管</th>
              {canWrite ? <th>操作</th> : null}
            </tr>
          </thead>
          <tbody>
            {rows.map((catheter) => {
              const days = daysSince(catheter.inserted_at);
              return (
                <tr key={catheter.id} className={days !== null && days >= 14 ? "overdue" : ""}>
                  <td>{formatDate(catheter.inserted_at)}</td>
                  <td>{days ?? "—"}</td>
                  <td>{catheter.indication}</td>
                  <td>{catheter.daily_reviewed_at ? "已评估" : "待评估"}</td>
                  <td>{catheter.removal_recommended ? "是" : "否"}</td>
                  {canWrite ? (
                    <td className="row-actions">
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => {
                          void act(
                            () => clinicalApi.reviewCatheter(catheter.id, false, "留置指征仍成立"),
                            "已完成评估",
                          );
                        }}
                      >
                        继续留置
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => {
                          void act(
                            () => clinicalApi.reviewCatheter(catheter.id, true, "建议尽早拔管"),
                            "已标记建议拔管",
                          );
                        }}
                      >
                        建议拔管
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => {
                          void act(
                            () =>
                              clinicalApi.removeCatheter(
                                catheter.id,
                                new Date().toISOString(),
                                "床旁确认拔除",
                              ),
                            "已记录拔管",
                          );
                        }}
                      >
                        记录拔管
                      </button>
                    </td>
                  ) : null}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
